import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { decodeMachine } from "@/transport/fleet"
import { expectJson, expectResponse } from "@/transport/http"

export type AuthUser = {
  id: string
  email: string
  display_name: string
  created_at: number
  updated_at: number
}

function decodeAuthUser(value: unknown): AuthUser {
  const user = jsonObject(value, "Server returned an invalid user")
  return {
    id: jsonString(user.id, "Server returned an invalid user"),
    email: jsonString(user.email, "Server returned an invalid user"),
    display_name: jsonString(user.display_name, "Server returned an invalid user"),
    created_at: jsonNumber(user.created_at, "Server returned an invalid user"),
    updated_at: jsonNumber(user.updated_at, "Server returned an invalid user"),
  }
}

function decodeAuthMe(value: unknown) {
  const result = jsonObject(value, "Server returned an invalid account response")
  return {
    user: decodeAuthUser(result.user),
    machines: result.machines === undefined
      ? undefined
      : jsonArray(result.machines, "Server returned invalid machines").map(decodeMachine),
  }
}

export async function logoutAuthenticatedUser(pushEndpoint: string) {
  return controlApiFetch("/api/auth/logout", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ pushEndpoint }),
  }).then((response) => response.ok).catch(() => false)
}

type AuthCheckOptions = {
  signal?: AbortSignal
  confirmUnauthorized?: boolean
  delay?: (milliseconds: number) => Promise<void>
}

const wait = (milliseconds: number) => new Promise<void>((resolve) => setTimeout(resolve, milliseconds))

export async function getAuthenticatedUser(path: string, options?: AuthCheckOptions) {
  const controller = new AbortController()
  const abort = () => controller.abort(options?.signal?.reason)
  options?.signal?.throwIfAborted()
  options?.signal?.addEventListener("abort", abort, { once: true })
  const timeout = setTimeout(() => controller.abort(new DOMException("Session verification timed out", "TimeoutError")), 10_000)
  try {
    const init = { signal: controller.signal, cache: "no-store" as const }
    let response = await controlApiFetch(path, init)
    if (response.status === 401 && options?.confirmUnauthorized) {
      await response.body?.cancel()
      await (options.delay ?? wait)(250)
      controller.signal.throwIfAborted()
      response = await controlApiFetch(path, init)
    }
    if (response.status === 401) return null
    return await expectJson(response, decodeAuthMe)
  } finally {
    clearTimeout(timeout)
    options?.signal?.removeEventListener("abort", abort)
  }
}

export async function exchangeAuthHandoff(code: string, verifier: string) {
  return expectJson(await controlApiFetch("/api/auth/handoff", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, verifier }),
  }), (value) => {
    const result = jsonObject(value, "OAuth handoff returned invalid data")
    return result.session === undefined ? "" : jsonString(result.session, "OAuth handoff returned invalid data")
  }, { error: "OAuth handoff failed" })
}

/** Creates a new test account and signs in to it (session cookie). */
export async function signInAsTestUser() {
  await expectResponse(await controlApiFetch("/api/auth/test", { method: "POST" }), { error: "Could not create a test account" })
}
